import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { urlConfig } from '../../config';
import {
  VipCoin,
  VipRates,
  VipSettings,
  VipEligibility,
  VipAddressResponse,
  VipDeposit,
  VipDepositsResponse,
} from './types';

export const vipTradingApi = createApi({
  reducerPath: 'vipTradingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: urlConfig.vipTradingUrl,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('token');
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['VipEligibility', 'VipRates', 'VipSettings', 'VipAddress', 'VipDeposits'],
  endpoints: (builder) => ({
    // Check if user is eligible for VIP trading
    checkVipEligibility: builder.query<VipEligibility, void>({
      query: () => 'vip-trading/eligible',
      providesTags: ['VipEligibility'],
    }),

    // Get current VIP rates
    getVipRates: builder.query<VipRates, void>({
      query: () => 'vip-trading/rates',
      providesTags: ['VipRates'],
    }),

    // Get VIP settings (min deposit, support link, etc.)
    getVipSettings: builder.query<VipSettings, void>({
      query: () => 'vip-trading/settings',
      providesTags: ['VipSettings'],
    }),

    // Get or generate VIP deposit address for a coin
    getVipAddress: builder.query<VipAddressResponse, VipCoin>({
      query: (coin) => `vip-trading/address/${coin}`,
      providesTags: (result, error, coin) => [{ type: 'VipAddress', id: coin }],
    }),

    // Get user's VIP deposits
    getVipDeposits: builder.query<VipDepositsResponse, { page?: number; limit?: number } | void>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.page) queryParams.append('page', params.page.toString());
        if (params?.limit) queryParams.append('limit', params.limit.toString());
        return `vip-trading/deposits?${queryParams.toString()}`;
      },
      providesTags: ['VipDeposits'],
    }),

    // Get single VIP deposit details
    getVipDeposit: builder.query<VipDeposit, string>({
      query: (id) => `vip-trading/deposits/${id}`,
      providesTags: (result, error, id) => [{ type: 'VipDeposits', id }],
    }),
  }),
});

export const {
  useCheckVipEligibilityQuery,
  useGetVipRatesQuery,
  useGetVipSettingsQuery,
  useGetVipAddressQuery,
  useLazyGetVipAddressQuery,
  useGetVipDepositsQuery,
  useGetVipDepositQuery,
} = vipTradingApi;
